import { getExtractionStatus } from '../components/DataPointsTable';

/**
 * Shapes one client's documents into the eMoney client report:
 * report sets (Net Worth, Cash Flow / Protection, Reviewed Separately) ->
 * sections (one per Build prompt, ordered by the backend's section map) ->
 * one entry per account, showing only the most recent statement.
 *
 * Works with today's single-account extraction output and with output that
 * lists several accounts under an `accounts` array. Keys are matched loosely
 * ("statement_period_end", "Statement Period End") so eMoney field names
 * work as JSON keys.
 */

const normalizeKey = (key) => String(key || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Build data prompts put matching info in a per-account "_meta" object
// (match_key, as_of_date, institution) that is never shown as a field.
export const META_KEY = '_meta';

// Most specific first: the first parseable one dates the statement. Covers
// the Extract_* snake_case keys and the eMoney labels the Build prompts use.
const DATE_KEYS = [
  'as_of_date',
  'As of',
  'statement_date',
  'statement_period_end',
  'period_end',
  'pay_period_end',
  'payment_date_or_period_end',
  'Check Date',
  'Document Date',
  'Tax Year',
].map(normalizeKey);

const ACCOUNT_KEYS = ['account_number', 'account_no', 'account_num', 'Account'].map(normalizeKey);

const INSTITUTION_KEYS = [
  'institution',
  'institution_name',
  'bank_name',
  'custodian_or_broker_name',
  'custodian_or_plan_administrator',
  'card_issuer',
  'lender_name',
  'insurer_name',
  'payer_or_employer_name',
  'employer_name',
].map(normalizeKey);

const isScalar = (value) => value === null || ['string', 'number', 'boolean'].includes(typeof value);

/** First non-empty scalar in `fields` whose key matches one of `keys` (in `keys` order). */
const findField = (fields, keys) => {
  const byKey = {};
  Object.entries(fields || {}).forEach(([key, value]) => {
    const norm = normalizeKey(key);
    if (!(norm in byKey) && isScalar(value) && value !== null && String(value).trim() !== '') {
      byKey[norm] = value;
    }
  });
  for (const key of keys) {
    if (key in byKey) return byKey[key];
  }
  return null;
};

/** Epoch seconds for a date-like value, or null. */
export const parseDate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return value > 1e12 ? value / 1000 : value;
  const parsed = Date.parse(String(value));
  return Number.isNaN(parsed) ? null : parsed / 1000;
};

const metaOf = (fields) => {
  const meta = fields && fields[META_KEY];
  return meta && typeof meta === 'object' && !Array.isArray(meta) ? meta : {};
};
const metaValue = (fields, key) => {
  const value = metaOf(fields)[key];
  return value === null || value === undefined || String(value).trim() === '' ? null : value;
};

export const accountNumberOf = (fields) => findField(fields, ACCOUNT_KEYS);
export const institutionOf = (fields) => metaValue(fields, 'institution') ?? findField(fields, INSTITUTION_KEYS);
export const asOfOf = (fields) => metaValue(fields, 'as_of_date') ?? findField(fields, DATE_KEYS);

/** Last four digits of an account number ("x1234", "****1234" -> "1234"); null when it has none. */
export const accountLast4 = (accountNumber) => {
  if (accountNumber === null || accountNumber === undefined) return null;
  const digits = String(accountNumber).replace(/\D/g, '');
  if (digits) return digits.slice(-4);
  const alnum = String(accountNumber).toLowerCase().replace(/[^a-z0-9]/g, '');
  return alnum || null;
};

/**
 * A comparable account identifier: masks stripped ("x9999", "****9999" ->
 * "9999"), numbers cut to their last four digits, other identifiers (EIN,
 * VIN, an address, "xT99") lowercased with punctuation removed.
 */
export const normalizeMatchKey = (value) => {
  if (value === null || value === undefined) return null;
  const alnum = String(value).toLowerCase().replace(/[^a-z0-9]/g, '').replace(/^x(?=[a-z0-9])/, '');
  if (!alnum) return null;
  return /^\d+$/.test(alnum) ? alnum.slice(-4) : alnum;
};

/** The account's match key: _meta.match_key when given, else its account number. */
export const matchKeyOf = (fields) => (
  normalizeMatchKey(metaValue(fields, 'match_key')) ?? accountLast4(accountNumberOf(fields))
);

// FP Build prompts name each entry the way it's entered in eMoney
// ("Jane's Chase Checking x9999"); that name titles the account's table.
const TITLE_KEYS = ['assetname', 'name', 'policyname', 'businessname'];

/** The account's eMoney name, or null when the extraction has none. */
export const titleOf = (fields) => {
  const entry = Object.entries(fields || {}).find(([key, value]) => (
    TITLE_KEYS.includes(normalizeKey(key)) && typeof value === 'string' && value.trim()
  ));
  return entry ? entry[1] : null;
};

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

/**
 * The accounts a document's data points describe, each with where it is
 * stored: { fields, listKey, index, ownKeys }. A listed account's fields
 * include the document's top-level single values (shared by every account);
 * ownKeys are the ones stored on the account itself. A document without an
 * accounts list is one account (listKey and index null).
 */
export const accountRefsOf = (dataPoints) => {
  if (!isPlainObject(dataPoints)) return [];
  const listKey = Object.keys(dataPoints).find((key) => normalizeKey(key) === 'accounts');
  const list = listKey ? dataPoints[listKey] : null;
  if (Array.isArray(list) && list.length && list.every(isPlainObject)) {
    const shared = {};
    Object.entries(dataPoints).forEach(([key, value]) => {
      if (key !== listKey && isScalar(value)) shared[key] = value;
    });
    return list.map((account, index) => ({
      fields: { ...shared, ...account }, listKey, index, ownKeys: Object.keys(account),
    }));
  }
  return [{ fields: dataPoints, listKey: null, index: null, ownKeys: Object.keys(dataPoints) }];
};

/** The accounts a document's data points describe. */
export const accountsOf = (dataPoints) => accountRefsOf(dataPoints).map((ref) => ref.fields);

const isListed = (ref) => Boolean(ref) && ref.listKey !== null && ref.listKey !== undefined;

/**
 * Where a field shown in an account table is stored, as the path a
 * correction uses: the account's own fields are inside the accounts list; a
 * shared top-level value is at the top (correcting it changes every account
 * of the document).
 */
export const valuePath = (ref, key, ...childPath) => {
  const tail = [key, ...childPath];
  if (isListed(ref) && ref.ownKeys.includes(key)) return [ref.listKey, ref.index, ...tail];
  return tail;
};

/** The path an entered-in-eMoney mark uses: always the account's own row, shared fields included. */
export const syncPath = (ref, key, ...childPath) => {
  const tail = [key, ...childPath];
  return isListed(ref) ? [ref.listKey, ref.index, ...tail] : tail;
};

/** True when a value path is a shared top-level field rather than the account's own. */
export const isSharedField = (ref, key) => isListed(ref) && !ref.ownKeys.includes(key);

export const samePath = (a, b) => (
  Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((segment, i) => segment === b[i])
);

export const EMPTY_ANNOTATIONS = { corrections: [], sync: [], notes: [] };

const isEmptyValue = (value) => value === null || value === undefined || value === '';
const isSingleValue = (value) => !Array.isArray(value) && !isPlainObject(value);

/**
 * The rows an account table shows, in order: { key, rowKey } per field, and
 * for a group { group: true, key } then { key, childKey } per field in it.
 * Empty values are hidden unless includeEmpty (edit mode, so they can be
 * filled in). Shared by the table and the PDF / Word export.
 */
export const fieldRowsOf = (fields, includeEmpty = false) => {
  const rows = [];
  Object.entries(fields || {}).forEach(([key, value]) => {
    if (key === META_KEY) return;
    if (isPlainObject(value)) {
      const children = Object.entries(value)
        .filter(([, v]) => (includeEmpty ? isSingleValue(v) || !isEmptyValue(v) : !isEmptyValue(v)));
      if (!children.length) return;
      rows.push({ group: true, key, rowKey: `group:${key}` });
      children.forEach(([childKey]) => rows.push({ key, childKey, rowKey: JSON.stringify([key, childKey]) }));
      return;
    }
    if (isEmptyValue(value) && !includeEmpty) return;
    rows.push({ key, rowKey: JSON.stringify([key]) });
  });
  return rows;
};

/**
 * Scalar fields shown in an account, including leaves in nested holdings rows.
 * item: what the eMoney counts count the value as part of -- a nested table's
 * row (a holding is entered in eMoney as one line), else the value itself.
 */
export const valueEntriesOf = (fields, ref, includeEmpty = false) => {
  const entries = [];
  const walk = (value, path) => {
    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (isPlainObject(item)) walk(item, [...path, index]);
      });
      return;
    }
    if (isPlainObject(value)) {
      Object.entries(value).forEach(([key, child]) => {
        if (key !== META_KEY) walk(child, [...path, key]);
      });
      return;
    }
    if (includeEmpty || !isEmptyValue(value)) {
      const row = path.findIndex((segment) => typeof segment === 'number');
      entries.push({
        path,
        item: JSON.stringify(syncPath(ref, ...(row < 0 ? path : path.slice(0, row + 1)))),
        rowKey: JSON.stringify(path),
        value,
        valuePath: valuePath(ref, ...path),
        syncPath: syncPath(ref, ...path),
      });
    }
  };
  Object.entries(fields || {}).forEach(([key, value]) => {
    if (key !== META_KEY) walk(value, [key]);
  });
  return entries;
};

/** True when a field path lies under prefix (or is prefix itself). */
export const startsWithPath = (path, prefix) => (
  Array.isArray(path) && Array.isArray(prefix) && prefix.length <= path.length
  && prefix.every((segment, i) => path[i] === segment)
);

/**
 * How far some values are checked off as entered in eMoney: { done, total,
 * stale }, counted in items (itemOf(entry); each entry its own by default) --
 * done: every value of the item marked and unchanged; stale: a value changed
 * after its check. markOf(entry): the entry's mark. Shared by nested rows and
 * tables, accounts and sections.
 */
export const completionOf = (entries, markOf, itemOf = null) => {
  const items = new Map();
  (entries || []).forEach((entry, idx) => {
    const key = itemOf ? itemOf(entry) : idx;
    if (!items.has(key)) items.set(key, []);
    items.get(key).push(markOf(entry));
  });
  const acc = { done: 0, total: items.size, stale: 0 };
  items.forEach((marks) => {
    if (marks.some((mark) => mark && mark.stale)) acc.stale += 1;
    else if (marks.every(Boolean)) acc.done += 1;
  });
  return acc;
};

/** A field row's correction and entered-in-eMoney mark, from the document's annotations. */
export const rowAnnotations = (annotations, ref, row) => {
  const { corrections, sync } = annotations || EMPTY_ANNOTATIONS;
  const path = row.path || [row.key, ...(row.childKey === undefined ? [] : [row.childKey])];
  return {
    correction: corrections.find((c) => samePath(c.path, valuePath(ref, ...path))),
    mark: sync.find((s) => samePath(s.path, syncPath(ref, ...path))),
  };
};

/** The note on one account table (accountIndex null for a single-account document). */
export const accountNote = (annotations, ref) => {
  const listed = ref && ref.listKey !== null && ref.listKey !== undefined;
  const index = listed ? ref.index : null;
  return ((annotations || EMPTY_ANNOTATIONS).notes || []).find((n) => !n.orphaned && n.accountIndex === index);
};

/** A value as text, the way the server compares eMoney marks (12000.0 -> "12000"). */
export const valueText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
};

/**
 * True when what was typed in the editor is the value already there, read
 * the way the server stores a correction (annotations.coerce_correction):
 * "$1,250.50" is 1250.5 where the value is a number.
 */
export const typedMatches = (typed, value) => {
  const text = typed === null || typed === undefined ? '' : String(typed);
  if (typeof value === 'number') {
    const cleaned = text.replace(/[$,\s]/g, '');
    return /^-?\d+(\.\d+)?$/.test(cleaned) && Number(cleaned) === value;
  }
  return text === valueText(value);
};

/**
 * The annotations a save is expected to produce, shown at once while the
 * server confirms (its reply replaces them). kind / payload as sent to the
 * server; hint.original is the value being corrected; by: who is saving.
 */
export const optimisticAnnotations = (annotations, kind, payload, { by, original } = {}) => {
  const current = annotations || EMPTY_ANNOTATIONS;
  const at = new Date().toISOString();
  const field = (path) => path.filter((segment) => typeof segment === 'string');
  if (kind === 'correction') {
    const existing = current.corrections.find((c) => samePath(c.path, payload.path));
    const others = current.corrections.filter((c) => c !== existing);
    if (payload.action === 'revert') return { ...current, corrections: others };
    const source = existing ? existing.original : original;
    const cleaned = typeof payload.value === 'string'
      ? payload.value.replace(/[$,\s]/g, '') : null;
    const value = typeof source === 'number' && cleaned !== null
      && /^-?\d+(\.\d+)?$/.test(cleaned)
      ? Number(cleaned) : payload.value;
    return {
      ...current,
      corrections: [...others, {
        path: payload.path,
        field: field(payload.path),
        value,
        original: source,
        by,
        at,
        version: (existing ? existing.version : 0) + 1,
        pending: true,
      }],
    };
  }
  if (kind === 'sync') {
    const others = current.sync.filter((s) => !samePath(s.path, payload.path));
    if (!payload.synced) return { ...current, sync: others };
    return {
      ...current,
      sync: [...others, {
        path: payload.path, field: field(payload.path), value: valueText(payload.expectedValue), by, at, stale: false, pending: true,
      }],
    };
  }
  if (kind === 'note') {
    const existing = current.notes.find((n) => !n.orphaned && n.accountIndex === payload.accountIndex);
    const others = current.notes.filter((n) => n !== existing);
    const text = String(payload.text || '').trim();
    if (!text) return { ...current, notes: others };
    return {
      ...current,
      notes: [...others, {
        accountIndex: payload.accountIndex, text, by, at, version: (existing ? existing.version : 0) + 1, orphaned: false, pending: true,
      }],
    };
  }
  return current;
};

/**
 * A copy of the data points with the report's corrections applied (each
 * correction carries its resolved path; orphaned ones, path null, are
 * skipped). The input is left as it was.
 */
export const applyCorrections = (dataPoints, corrections) => {
  const placed = (corrections || []).filter((c) => Array.isArray(c.path) && c.path.length);
  if (!placed.length || !isPlainObject(dataPoints)) return dataPoints;
  const copy = JSON.parse(JSON.stringify(dataPoints));
  placed.forEach(({ path, value }) => {
    let node = copy;
    for (const segment of path.slice(0, -1)) {
      node = node === null || node === undefined ? undefined : node[segment];
    }
    if (isPlainObject(node)) node[path[path.length - 1]] = value;
  });
  return copy;
};

/**
 * The value the report shows for a path (the server's
 * annotations.merged_value): a listed account's field it doesn't have
 * itself falls back to the document's shared top-level value.
 */
export const mergedValue = (dataPoints, path) => {
  if (!isPlainObject(dataPoints) || !Array.isArray(path)) return undefined;
  const listKey = Object.keys(dataPoints).find((key) => normalizeKey(key) === 'accounts');
  const list = listKey ? dataPoints[listKey] : null;
  if (Array.isArray(list) && list.length && list.every(isPlainObject) && path.length === 3
      && path[0] === listKey && Number.isInteger(path[1]) && list[path[1]] && !(path[2] in list[path[1]])) {
    const shared = dataPoints[path[2]];
    if (isScalar(shared)) return shared;
  }
  return path.reduce((node, segment) => (node !== null && typeof node === 'object' ? node[segment] : undefined), dataPoints);
};

/**
 * Entered-in-eMoney marks re-checked against the document's values with
 * these annotations' corrections applied, the way the server checks them: a
 * mark is stale once the value at its path differs from the one checked.
 * For changes the server hasn't confirmed yet.
 */
export const refreshSyncStaleness = (annotations, dataPoints) => {
  const current = annotations || EMPTY_ANNOTATIONS;
  if (!current.sync.length) return current;
  const corrected = applyCorrections(dataPoints, current.corrections);
  return {
    ...current,
    sync: current.sync.map((mark) => ({
      ...mark,
      stale: !Array.isArray(mark.path) || valueText(mergedValue(corrected, mark.path)) !== mark.value,
    })),
  };
};

const statementTime = (fields, doc) => (
  parseDate(asOfOf(fields)) ?? parseDate(doc.uploadedAt) ?? parseDate(doc.extractedAt) ?? 0
);

const compareText = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, { sensitivity: 'base' });

/**
 * docs: GET /bedrock/extractions rows (with the backend's reportSet /
 * section fields).
 * -> [{ key, label, sections: [{ key, label, accounts: [{ key, current, older }], withoutData }] }]
 * where current / older entries are { doc, fields, ref, asOf, time } (ref:
 * where the account is stored in doc.dataPoints, see accountRefsOf).
 */
export const buildClientReport = (docs) => {
  const sets = new Map();

  const sectionFor = (doc) => {
    const setKey = doc.reportSet || 'reviewed_separately';
    if (!sets.has(setKey)) {
      sets.set(setKey, {
        key: setKey,
        label: doc.reportSetLabel || 'Reviewed Separately',
        order: doc.reportSetOrder ?? 99,
        sections: new Map(),
      });
    }
    const set = sets.get(setKey);
    const sectionKey = doc.section || 'reviewed_separately';
    if (!set.sections.has(sectionKey)) {
      set.sections.set(sectionKey, {
        key: sectionKey,
        label: doc.sectionLabel || 'Reviewed Separately',
        order: doc.sectionOrder ?? 999,
        groups: new Map(),
        withoutData: [],
      });
    }
    return set.sections.get(sectionKey);
  };

  (docs || []).forEach((doc) => {
    const section = sectionFor(doc);
    if (getExtractionStatus(doc.dataPoints, doc.dataPointsDetails).kind !== 'extracted') {
      section.withoutData.push(doc);
      return;
    }
    accountRefsOf(doc.dataPoints).forEach(({ fields, ...ref }, idx) => {
      const matchKey = matchKeyOf(fields);
      const key = matchKey ? `${section.key}|${matchKey}` : `${section.key}|${doc.fileId}|${idx}`;
      if (!section.groups.has(key)) section.groups.set(key, []);
      section.groups.get(key).push({ doc, fields, ref, asOf: asOfOf(fields), time: statementTime(fields, doc) });
    });
  });

  return Array.from(sets.values())
    .sort((a, b) => a.order - b.order)
    .map((set) => ({
      key: set.key,
      label: set.label,
      sections: Array.from(set.sections.values())
        .sort((a, b) => a.order - b.order)
        .map((section) => ({
          key: section.key,
          label: section.label,
          accounts: Array.from(section.groups.entries())
            .map(([key, entries]) => {
              const [current, ...older] = entries.slice().sort((a, b) => b.time - a.time);
              return { key, current, older };
            })
            .sort((a, b) => (
              compareText(institutionOf(a.current.fields), institutionOf(b.current.fields))
              || compareText(matchKeyOf(a.current.fields), matchKeyOf(b.current.fields))
            )),
          withoutData: section.withoutData,
        })),
    }));
};
